import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { datumAusSpalten, datumKurz } from "@stammbaum/shared";

/**
 * Kleines Suchfeld, um eine Person auszuwählen — für die Zuordnung einer
 * Bildmarkierung. Bewusst ohne Auswahlliste aller 1500 Personen: tippen
 * und aus wenigen Treffern wählen ist schneller als scrollen.
 */
export function PersonSucher({
  onWahl,
  onAbbrechen,
  hinweis,
  unten,
}: {
  onWahl: (person: { id: number; vorname: string; famname: string }) => void;
  onAbbrechen: () => void;
  /** Was unter dem leeren Feld steht. Der Standardtext passt zur Bildmarkierung. */
  hinweis?: ReactNode;
  /**
   * Zusatz am Fuss der Lade - fuer den Fall, dass die gesuchte Person
   * noch gar nicht angelegt ist. Steht immer da, auch waehrend getippt
   * wird: nach drei erfolglosen Buchstaben ist genau das der Moment, in
   * dem man sie anlegen will.
   */
  unten?: ReactNode;
}) {
  const [text, setText] = useState("");
  const { data, isFetching } = useQuery({
    queryKey: ["personen", text],
    queryFn: () => api.personen(text),
    enabled: text.trim().length > 0,
  });

  return (
    <div className="w-72 rounded-lg border border-black/15 bg-white p-2 shadow-lg dark:border-white/15 dark:bg-neutral-800">
      <div className="flex gap-2">
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && onAbbrechen()}
          placeholder="Name — auch mehrteilig, z.B. Huber Hans"
          className="w-full rounded border border-black/15 px-2 py-1 text-sm outline-none
                     focus:border-black/40 dark:border-white/15 dark:bg-neutral-900"
        />
        <button
          onClick={onAbbrechen}
          title="Abbrechen"
          className="px-1 text-black/40 hover:text-black dark:text-white/40"
        >
          ×
        </button>
      </div>

      <ul className="mt-2 max-h-60 overflow-y-auto text-sm">
        {data?.treffer.slice(0, 20).map((p) => (
          <li key={p.id}>
            <button
              onClick={() => onWahl({ id: p.id, vorname: p.vorname, famname: p.famname })}
              className="flex w-full items-baseline gap-2 rounded px-2 py-1 text-left
                         hover:bg-black/5 dark:hover:bg-white/10"
            >
              <span className="flex-1">
                {p.famname} {p.vorname}
              </span>
              {/* Dasselbe wie in der Trefferliste - hier wird ja erst recht
                  auseinandergehalten, welcher Huber gemeint ist. */}
              <span className="whitespace-nowrap text-xs tabular-nums text-black/40 dark:text-white/40">
                {datumKurz(datumAusSpalten(p, "geburt"))}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {text.trim() && !isFetching && data?.treffer.length === 0 && (
        <p className="px-2 py-1 text-sm text-black/50">Keine Person gefunden.</p>
      )}
      {!text.trim() && (
        <p className="px-2 py-1 text-xs text-black/40 dark:text-white/40">
          {hinweis ?? (
            <>
              Name eintippen, gern mehrteilig („Huber Hans 1887"). Ohne
              Zuordnung speichern geht auch — dann bleibt die Markierung offen.
            </>
          )}
        </p>
      )}

      {unten && (
        <div className="mt-1 border-t border-black/10 pt-1 dark:border-white/10">{unten}</div>
      )}
    </div>
  );
}
